import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { groupBy, sortBy } from 'lodash';

import {
  ItemType,
  type ScenarioTimelineGroupFields,
  type ScenarioTimelineItem,
  type TimelineCompletedVoyage,
  type TimelineSuggestion,
  type TimelineVoyage,
} from '../types';
import type { TimelineGroup, TimelineItem } from '../react-calendar-timeline';
import type { SuggestionsByVessel } from '../../core/suggestions/getSuggestions';
import type { usePlanningMode } from '../../../state/zustand';
import type { ScenarioTimelineProps } from '../ScenarioTimeline';
import { assertDefined } from '@seaber/common/lib/generics';
import { Objective } from '../../core/suggestions/VoyageSuggestionParams';
import type { EventOrLeg } from '../../core/TimeData';
import {
  getEventLocationId,
  isCargoEventTimeData,
  isTCOrderEventTimeData,
} from '../../core/TimeData';
import {
  freeEventFilter,
  getRelatedOrder,
  groupOrdersByPortCallId,
  isLegOrWait,
} from './utils';
import { getCompletedTimelineItem } from './getCompletedTimelineItem';
import { getOverlappingEvents } from '../../core/util';
import type { CompletedData } from '../../../lib/planningData';
import type { ScenarioSchedule } from '@seaber/common/core/scenario';
import { useModal } from '../../contexts/ModalContext';
import { prepareSuggestionMetrics } from '../suggestions/estimatorHelpers';

type UseSuggestionItemsProps = {
  suggestions?: SuggestionsByVessel;
  groups: TimelineGroup<ScenarioTimelineGroupFields>[];
  showNSuggestions: number;
  expandedSuggestions: Set<string>;
  onSuggestionSelect: (vesselId: number, suggestionIndex: number) => void;
  onDismissSuggestion: (vesselId: number, suggestionIndex: number) => void;
  dismissedSuggestionIds: Set<string>;
  planningMode: ReturnType<typeof usePlanningMode>;
  scheduleContext: ScenarioTimelineProps['scheduleContext'];
  setExpandedSuggestions: React.Dispatch<React.SetStateAction<Set<string>>>;
};

export const useSuggestionItems = ({
  suggestions,
  groups,
  showNSuggestions,
  expandedSuggestions,
  onSuggestionSelect,
  onDismissSuggestion,
  dismissedSuggestionIds,
  scheduleContext,
  planningMode,
  setExpandedSuggestions,
}: UseSuggestionItemsProps): TimelineItem<TimelineSuggestion>[] => {
  return useMemo(() => {
    if (!suggestions) return [];

    const suggestionArray = Array.from(suggestions);

    const allSuggestions = suggestionArray.flatMap((pair) => pair[1]);

    let maxVCP = -Infinity;
    let maxTCE = -Infinity;
    let lowestCost = Infinity;
    let lowestBallastDelta = Infinity;
    let lowestDistanceDelta = Infinity;
    allSuggestions.forEach((suggestion) => {
      maxTCE = Math.max(maxTCE, suggestion.tce);
      maxVCP = Math.max(maxVCP, +suggestion.vesselPerformanceMetrics.delta.vcp);
      lowestCost = Math.min(lowestCost, suggestion.cost);
      lowestBallastDelta = Math.min(
        lowestBallastDelta,
        suggestion.ballastDelta,
      );
      lowestDistanceDelta = Math.min(
        lowestDistanceDelta,
        suggestion.distanceDelta,
      );
    });

    return suggestionArray.flatMap(([vesselId, suggestions]) => {
      const groupObj = groups.find(
        (g) => g.timeline.type === 'tc' && g.timeline.vesselId === vesselId,
      );
      assertDefined(groupObj, 'groupObj');
      const shownSuggestions = suggestions.slice(0, showNSuggestions + 1);

      let minStart = Infinity;
      let maxEnd = -Infinity;

      suggestions.forEach((suggestion) => {
        const { entries } = suggestion;
        minStart = Math.min(minStart, ...entries.map((e) => e.loadingStart));
        maxEnd = Math.max(maxEnd, ...entries.map((e) => e.dischargingEnd));
      });

      return shownSuggestions.flatMap((suggestion, i) => {
        const { entries: allEntries } = suggestion;
        const suggestionId = `${vesselId}-${i}`;

        // If the suggestion is dismissed, don't show it
        if (dismissedSuggestionIds.has(suggestionId)) {
          return [];
        }

        const items: TimelineItem<TimelineSuggestion>[] = (
          expandedSuggestions.has(suggestionId) ? allEntries : [undefined]
        ).map((entrySelection) => {
          const entries = entrySelection ? [entrySelection] : allEntries;
          return {
            id: `suggestion-${allEntries.map((e) => e.orderId).join('-')}-${entrySelection?.orderId}-${vesselId}`,
            type: ItemType.SUGGESTION,
            group: groupObj.id,
            groupObj,
            start_time: minStart,
            end_time: maxEnd,
            start: Math.min(...entries.map((e) => e.loadingStart)),
            end: Math.max(...entries.map((e) => e.dischargingEnd)),
            onSelect: onSuggestionSelect,
            onDismiss: onDismissSuggestion,
            suggestion,
            entries,
            vesselId,
            suggestionIndex: i,
            isBestFound:
              suggestion.objective === Objective.MinimizeCost
                ? suggestion.cost === lowestCost
                : suggestion.objective === Objective.MaximizeVCP
                  ? +suggestion.vesselPerformanceMetrics.delta.vcp === maxVCP
                  : suggestion.objective === Objective.ShortestBallast
                    ? suggestion.ballastDelta === lowestBallastDelta
                    : suggestion.objective === Objective.ShortestDistance
                      ? suggestion.distanceDelta === lowestDistanceDelta
                      : suggestion.tce === maxTCE,
            scheduleContext,
            isToggleExpanded: expandedSuggestions.has(suggestionId),
            showInquiries: planningMode === 'voyage_charter',
            onToggleShipment: (isCollapsed) => {
              setExpandedSuggestions((expanded) => {
                const result = new Set(expanded);
                if (isCollapsed) {
                  result.add(suggestionId);
                } else {
                  result.delete(suggestionId);
                }
                return result;
              });
            },
          };
        });
        return sortBy(items, (i) => i.start);
      });
    });
  }, [
    suggestions,
    groups,
    showNSuggestions,
    expandedSuggestions,
    onSuggestionSelect,
    onDismissSuggestion,
    scheduleContext,
    planningMode,
    setExpandedSuggestions,
    dismissedSuggestionIds,
  ]);
};

export const useCompletedItems = ({
  completedData,
  isCompressedView,
  locations,
}: {
  completedData?: CompletedData;
  isCompressedView?: boolean;
  locations: ScenarioTimelineProps['scheduleContext']['locations'];
}): TimelineItem<TimelineCompletedVoyage>[] => {
  return useMemo(() => {
    const items: TimelineItem<TimelineCompletedVoyage>[] = [];
    for (const timeline of completedData?.timelines ?? []) {
      const { type, voyageGroups } = timeline;
      if (type !== 'tc') continue;
      for (const voyageGroup of voyageGroups) {
        const { events, eventsAndLegs, orders } = voyageGroup;
        const mainEvents = events.filter(isTCOrderEventTimeData);
        const ordersByPortCallId = groupOrdersByPortCallId(mainEvents);

        const orderTdsByShipment = groupBy(
          events,
          (e) => getRelatedOrder(ordersByPortCallId, e.event)?.shipmentId ?? '',
        );

        /** Events which will be included into all overlapping items on the timeline */
        const timelineFreeEvents: EventOrLeg[] = [
          ...events.filter(freeEventFilter),
          ...eventsAndLegs.filter(isLegOrWait),
        ];

        const sortedOrderTdsByShipment = sortBy(
          Object.entries(orderTdsByShipment),
          (x) => x[1][0].start,
        );

        let prevItemLocation: number | undefined = undefined;
        for (const [shipmentId, shipmentTds] of sortedOrderTdsByShipment) {
          if (!shipmentId) continue;
          const overlappingEvents = getOverlappingEvents(
            shipmentTds,
            timelineFreeEvents,
            true,
          );

          const orderEventsWithGlobals = sortBy(
            [...shipmentTds, ...overlappingEvents],
            (x) => x.start,
          );

          items.push(
            getCompletedTimelineItem(
              shipmentTds,
              orderEventsWithGlobals,
              locations,
              shipmentId,
              timeline,
              orders,
              isCompressedView,
              prevItemLocation,
            ),
          );

          const lastCargoEvent =
            orderEventsWithGlobals.findLast(isCargoEventTimeData)?.event;

          prevItemLocation = lastCargoEvent
            ? getEventLocationId(lastCargoEvent)
            : undefined;
        }
      }
    }
    return items;
  }, [completedData, isCompressedView, locations]);
};

export const useFilteredItems = ({
  items,
  filteredGroups,
  spotAssignments,
  completedItems,
}: {
  items: Array<ScenarioTimelineItem | TimelineItem<TimelineSuggestion>>;
  filteredGroups: TimelineGroup<ScenarioTimelineGroupFields>[];
  spotAssignments: ScenarioSchedule['spotAssignments'];
  completedItems: TimelineItem<TimelineCompletedVoyage>[];
}) => {
  return useMemo(() => {
    const shadowItems: ScenarioTimelineItem[] = items
      .filter(
        (item: ScenarioTimelineItem) =>
          (item.type === ItemType.COA && item.groupObj.timeline.vesselId) ||
          item.type === ItemType.SPOT,
      )
      .flatMap((x: TimelineItem<TimelineVoyage>) => {
        return filteredGroups
          .filter(
            (gItem: TimelineGroup<ScenarioTimelineGroupFields>) =>
              (gItem.timeline.type === 'coa' &&
                x.groupObj.timeline.type === 'coa' &&
                gItem.timeline.vesselId === x.groupObj.timeline.vesselId &&
                gItem.timeline.coaId !== x.groupObj.timeline.coaId) ||
              (x.groupObj.timeline.type === 'spot' &&
                gItem.timeline.coa?.shipowner?.id ===
                  x.groupObj.timeline.ownerOrganizationId &&
                'orders' in x &&
                gItem.timeline.vesselId &&
                spotAssignments.find((s) => s.orderId === x.orders[0].id)
                  ?.vesselId === gItem.timeline.vesselId),
          )
          .map((group: TimelineGroup<ScenarioTimelineGroupFields>) => ({
            ...x,
            id: `inactive-${group.id}-item-${x.id}`,
            originalItemId: x.id,
            originalContract: x.groupObj.timeline.coa?.name ?? '',
            isShadow: true,
            group: group.id,
            groupObj: { ...group },
          }));
      });

    return [...completedItems, ...items, ...shadowItems];
  }, [completedItems, items, filteredGroups, spotAssignments]);
};

export function useSingleClickHandlers(disableClick?: boolean) {
  const clickPassThrough = useRef<boolean>(false);

  return useMemo(
    () => ({
      onMouseDown: (e: React.MouseEvent) => {
        if (clickPassThrough.current) {
          return;
        }
        e.stopPropagation();
      },
      onMouseUp: () => {
        if (clickPassThrough.current) {
          clickPassThrough.current = false;
          return;
        }
      },
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        if (disableClick || clickPassThrough.current) {
          return;
        }

        clickPassThrough.current = true;

        for (const eventType of ['mousedown', 'click', 'mouseup']) {
          e.target.dispatchEvent(
            new MouseEvent(eventType, {
              view: window,
              bubbles: true,
              cancelable: true,
              buttons: 1,
              shiftKey: e.shiftKey,
            }),
          );
        }
      },
    }),
    [disableClick],
  );
}

export const useOpenSuggestionEstimator = () => {
  const { openModal } = useModal();

  return useCallback(
    ({
      selectedSuggestionIds,
      items,
      onSuggestionAccept,
    }: {
      selectedSuggestionIds: Set<string | number>;
      items: ScenarioTimelineItem[];
      onSuggestionAccept?: (vesselId: number, suggestionIndex: number) => void;
    }): void => {
      if (selectedSuggestionIds.size === 0) {
        return;
      }

      const selectedSuggestions = items.filter((item) =>
        selectedSuggestionIds.has(item.id),
      ) as TimelineItem<TimelineSuggestion>[];

      const selectedSuggestionsWithMetrics =
        prepareSuggestionMetrics(selectedSuggestions);

      if (selectedSuggestionsWithMetrics.length === 0) {
        return;
      }

      openModal({
        type: 'SUGGESTION_ESTIMATOR',
        props: {
          suggestions: selectedSuggestionsWithMetrics,
          onSuggestionAccept,
        },
      });
    },
    [openModal],
  );
};
